import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import PWAInstallPrompt from './PWAInstallPrompt';
import { 
  Home, Music, Radio, MessageSquare, Image, Video, Newspaper, 
  Settings, LogIn, LogOut, Menu, X, ChevronDown, User, Sun, Moon, Users, Sparkles
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const { theme, isDarkMode, toggleDarkMode, isCustomTheme, customSettings } = useTheme();
  const { user, logout, isAdmin, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteConfig, setSiteConfig] = useState({ site_title: 'BEATLAB52', footer_text: '' });
  const configFetched = useRef(false);

  useEffect(() => {
    if (configFetched.current) return;
    configFetched.current = true;
    
    const fetchSiteConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-config`);
        setSiteConfig(res.data);
      } catch (e) {}
    };
    fetchSiteConfig();
  }, []);

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { 
      label: 'Media', 
      icon: Music,
      children: [
        { path: '/music', label: 'Music', icon: Music },
        { path: '/videos', label: 'Videos', icon: Video },
      ]
    },
    { 
      label: 'Entertainment', 
      icon: Radio,
      children: [
        { path: '/radio', label: 'Radio', icon: Radio },
        { path: '/chat', label: 'Chat Room', icon: MessageSquare },
        { path: '/events', label: 'Events', icon: Newspaper },
      ]
    },
    { path: '/gallery', label: 'Gallery', icon: Image },
    { path: '/news', label: 'News', icon: Newspaper },
    { path: '/about', label: 'About', icon: Users },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const overlayAlpha = isCustomTheme ? (customSettings.overlayOpacity / 100) : 1;

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    
    if (item.children) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`flex items-center gap-1 px-2 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${isCustomTheme ? 'hover:scale-105' : ''}`}
              style={{ 
                color: item.children.some(c => location.pathname === c.path) ? theme.primary : (isCustomTheme ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)'),
              }}
              data-testid={`nav-${item.label.toLowerCase()}-dropdown`}
            >
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
              <ChevronDown className="w-3 h-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent style={{ backgroundColor: isDarkMode ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)', borderColor: 'var(--border-color)' }} className="backdrop-blur-xl">
            {item.children.map((child) => (
              <DropdownMenuItem key={child.path} asChild>
                <Link
                  to={child.path}
                  className="flex items-center gap-2"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid={`nav-${child.label.toLowerCase()}`}
                >
                  <child.icon className="w-4 h-4" />
                  {child.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <Link
        to={item.path}
        className={`flex items-center gap-1 px-2 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${isCustomTheme ? 'hover:scale-105' : ''}`}
        style={{ 
          color: isActive ? theme.primary : (isCustomTheme ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)'),
          textShadow: isActive && isDarkMode ? theme.glow : 'none',
        }}
        data-testid={`nav-${item.label.toLowerCase()}`}
      >
        <item.icon className="w-3.5 h-3.5" />
        {item.label}
      </Link>
    );
  };

  const renderCustomThemeBackground = () => {
    if (!isCustomTheme) return null;
    return (
      <>
        {customSettings.bodyImage && (
          <div
            className="fixed inset-0 z-0"
            style={{
              backgroundImage: `url(${customSettings.bodyImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed',
            }}
          />
        )}
        <div
          className="fixed inset-0 z-0"
          style={{
            backgroundColor: `rgba(0, 0, 0, ${overlayAlpha.toFixed(2)})`,
          }}
        />
        <div
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse at 20% 50%, ${theme.primary}08 0%, transparent 50%),
              radial-gradient(ellipse at 80% 20%, ${theme.secondary}06 0%, transparent 50%)
            `,
          }}
        />
      </>
    );
  };

  const renderDefaultBackground = () => {
    if (isCustomTheme) return null;
    return (
      <>
        {isDarkMode && (
          <div 
            className="fixed inset-0 z-0 pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 0%, ${theme.primary}08 0%, transparent 50%)`,
            }}
          />
        )}
        {isDarkMode && (
          <div 
            className="fixed inset-0 z-0 opacity-30"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1550524756-d07d631274cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxyZWNvcmRpbmclMjBzdHVkaW8lMjBlcXVpcG1lbnQlMjBtaXhpbmclMjBjb25zb2xlJTIwZGFya3xlbnwwfHx8fDE3NzE4MTYxNzN8MA&ixlib=rb-4.1.0&q=85',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.4)',
            }}
          />
        )}
      </>
    );
  };

  const headerFrameStyles = {
    straight: {},
    rounded: { borderRadius: '0 0 24px 24px', overflow: 'hidden' },
    pill: { borderRadius: '0 0 40px 40px', overflow: 'hidden' },
    arch: { borderRadius: '0 0 50% 50% / 0 0 30px 30px', overflow: 'hidden' },
    wave: { borderRadius: '0 0 60% 60% / 0 0 20px 20px', overflow: 'hidden' },
    diamond: { clipPath: 'polygon(0 0, 100% 0, 98% 85%, 50% 100%, 2% 85%)', overflow: 'hidden' },
    slant_right: { clipPath: 'polygon(0 0, 100% 0, 100% 80%, 0 100%)', overflow: 'hidden' },
    slant_left: { clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 80%)', overflow: 'hidden' },
    notch: { clipPath: 'polygon(0 0, 100% 0, 100% 85%, 55% 100%, 45% 100%, 0 85%)', overflow: 'hidden' },
    scallop: { borderRadius: '0 0 100px 100px / 0 0 40px 40px', overflow: 'hidden' },
  };

  const headerOverlayAlpha = (customSettings.headerOverlayOpacity ?? 60) / 100;
  const activeHeaderStyle = customSettings.headerStyle || 'straight';
  const frameStyle = headerFrameStyles[activeHeaderStyle] || {};

  const renderCustomHeader = () => {
    if (!isCustomTheme || !customSettings.headerImage) return null;
    return (
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${customSettings.headerImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,${(headerOverlayAlpha * 0.8).toFixed(2)}), rgba(0,0,0,${Math.min(headerOverlayAlpha + 0.15, 1).toFixed(2)}))` }} />
      </div>
    );
  };

  const navBg = isCustomTheme
    ? (customSettings.headerImage
        ? 'transparent'
        : `rgba(10, 10, 10, 0.7)`)
    : (isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.8)');

  const navBorder = isCustomTheme
    ? `1px solid rgba(255,255,255,0.06)`
    : undefined;

  return (
    <div 
      className="min-h-screen transition-colors duration-300 overflow-x-hidden" 
      style={{ 
        fontFamily: 'var(--theme-font, Orbitron, sans-serif)',
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        maxWidth: '100vw',
      }}
    >
      {renderCustomThemeBackground()}
      {renderDefaultBackground()}

      <nav 
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-all duration-500 ${isCustomTheme ? 'backdrop-saturate-150' : ''} ${activeHeaderStyle === 'straight' ? 'border-b' : ''}`}
        style={{
          backgroundColor: navBg,
          borderColor: isCustomTheme ? 'rgba(255,255,255,0.06)' : 'var(--border-color)',
          position: 'fixed',
          ...((isCustomTheme && customSettings.headerImage) ? frameStyle : {}),
          ...(isCustomTheme && customSettings.headerImage && activeHeaderStyle !== 'straight' ? { boxShadow: `0 4px 30px rgba(0,0,0,0.4), 0 2px 0 ${theme.primary}20` } : {}),
        }}
      >
        {renderCustomHeader()}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className={`flex items-center justify-between ${isCustomTheme && customSettings.headerImage ? 'h-20' : 'h-16'}`}>
            <Link 
              to="/" 
              className="flex items-center gap-2"
              data-testid="nav-logo"
            >
              <div 
                className={`font-black tracking-wider ${isCustomTheme ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`}
                style={{ 
                  color: theme.primary,
                  textShadow: isDarkMode ? theme.glow : 'none',
                }}
              >
                {siteConfig.site_title?.toUpperCase() || 'BEATLAB52'}
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item, i) => (
                <NavLink key={i} item={item} />
              ))}
            </div>

            <div className="hidden md:flex items-center gap-2">
              {!isCustomTheme && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleDarkMode}
                  className="rounded-full"
                  style={{ color: theme.primary }}
                  data-testid="dark-mode-toggle"
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </Button>
              )}
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2"
                      style={{ color: theme.primary }}
                      data-testid="user-menu-trigger"
                    >
                      <User className="w-4 h-4" />
                      {user.username}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-black/90 backdrop-blur-xl border-white/10">
                    {isAdmin && (
                      <>
                        {hasPermission('ai_studio') && (
                          <DropdownMenuItem asChild>
                            <Link to="/admin/ai-studio" className="flex items-center gap-2 text-gray-300" data-testid="nav-ai-studio">
                              <Sparkles className="w-4 h-4" />
                              AI Studio
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 text-gray-300" data-testid="nav-admin">
                            <Settings className="w-4 h-4" />
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2 text-gray-300" data-testid="nav-settings">
                        <Settings className="w-4 h-4" />
                        Theme Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="flex items-center gap-2 text-gray-300 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button 
                    className={`flex items-center gap-2 font-bold uppercase tracking-wider ${isCustomTheme ? 'rounded-full px-6' : ''}`}
                    style={{ 
                      backgroundColor: theme.primary,
                      color: '#000',
                      boxShadow: theme.glow,
                    }}
                    data-testid="login-btn"
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                  </Button>
                </Link>
              )}
            </div>

            <div className="flex md:hidden items-center gap-2">
              {!isCustomTheme && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleDarkMode}
                  className="rounded-full"
                  style={{ color: theme.primary }}
                  data-testid="dark-mode-toggle-mobile"
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </Button>
              )}
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" style={{ color: theme.primary }} /> : <Menu className="w-6 h-6" style={{ color: theme.primary }} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/10 relative z-10"
            >
              <div className="px-4 py-4 space-y-2">
                {navItems.map((item, i) => (
                  item.children ? (
                    <div key={i} className="space-y-1">
                      <div className="text-xs uppercase tracking-wider text-gray-500 px-3 py-1">
                        {item.label}
                      </div>
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 text-gray-300"
                        >
                          <child.icon className="w-4 h-4" />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-gray-300"
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  )
                ))}
                <div className="border-t border-white/10 pt-2 mt-2">
                  {user ? (
                    <>
                      {isAdmin && (
                        <>
                          {hasPermission('ai_studio') && (
                            <Link
                              to="/admin/ai-studio"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-2 px-3 py-2 text-gray-300"
                              data-testid="mobile-nav-ai-studio"
                            >
                              <Sparkles className="w-4 h-4" />
                              AI Studio
                            </Link>
                          )}
                          <Link
                            to="/admin"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-gray-300"
                          >
                            <Settings className="w-4 h-4" />
                            Admin Panel
                          </Link>
                        </>
                      )}
                      <button
                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        className="flex items-center gap-2 px-3 py-2 text-gray-300 w-full"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2"
                      style={{ color: theme.primary }}
                    >
                      <LogIn className="w-4 h-4" />
                      Login
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className={`relative z-10 ${isCustomTheme && customSettings.headerImage ? 'pt-24 sm:pt-28' : 'pt-20 sm:pt-24'}`} style={{ minHeight: 'calc(100vh - 100px)' }}>
        {children}
      </main>

      <footer 
        className={`relative z-10 backdrop-blur-xl border-t py-4 sm:py-6 mt-6 transition-colors duration-300 ${isCustomTheme ? 'backdrop-saturate-150' : ''}`}
        style={{
          backgroundColor: isCustomTheme ? `rgba(0,0,0,${Math.min(overlayAlpha + 0.2, 1).toFixed(2)})` : (isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.8)'),
          borderColor: isCustomTheme ? 'rgba(255,255,255,0.06)' : 'var(--border-color)',
        }}
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-6 text-center">
          <div 
            className="text-base sm:text-lg font-bold mb-1"
            style={{ color: theme.primary, textShadow: isDarkMode ? theme.glow : 'none' }}
          >
            {siteConfig.site_title?.toUpperCase() || 'BEATLAB52'}
          </div>
          <p style={{ color: 'var(--text-muted)' }} className="text-sm">
            {siteConfig.footer_text?.replace('{year}', new Date().getFullYear()) || `DJ808 & CRG Productionz © ${new Date().getFullYear()} | All Rights Reserved`}
          </p>
        </div>
      </footer>

      <PWAInstallPrompt />
    </div>
  );
};

export default Layout;
